import { Link, useParams, useNavigate } from "react-router-dom";
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { excursions, reviews } from "@/mocks/excursions";
import { useSupabasePrices } from "@/hooks/useSupabasePrices";
import ProductGallery from "@/pages/excursion/components/ProductGallery";
import BookingWidget from "@/pages/excursion/components/BookingWidget";
import ExcursionCard from "@/components/feature/ExcursionCard";

export default function ExcursionPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const excursion = useMemo(() => {
    return excursions.find((e) => e.slug === slug);
  }, [slug]);
  const { data: allWithPrices } = useSupabasePrices(excursions);
  const excursionWithPrices = useMemo(() => {
    if (!excursion) return undefined;
    return allWithPrices.find((e) => e.slug === slug);
  }, [excursion, allWithPrices]);
  const related = useMemo(() => {
    return allWithPrices.filter((e) => e.slug !== slug).slice(0, 2);
  }, [allWithPrices, slug]);
  const productReviews = useMemo(
    () => reviews.filter((r) => r.excursion === slug),
    [slug]
  );

  const goToHomeExcursions = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#excursions");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  }, [navigate]);

  if (!excursion) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading text-3xl text-foreground-950 mb-3">{t("ex_not_found_title")}</h1>
          <p className="text-foreground-600 mb-6">{t("ex_not_found_body")}</p>
          <Link to="/" className="text-accent-700 underline cursor-pointer">
            {t("ex_back_home")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50" data-product-shop>
      <Navbar />

      <div className="pt-24 md:pt-28 bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center gap-2 text-xs md:text-sm flex-wrap">
          <Link to="/" className="text-foreground-600 hover:text-accent-700 cursor-pointer">{t("ex_home")}</Link>
          <i className="ri-arrow-right-s-line text-foreground-500"></i>
          <Link to="/" onClick={goToHomeExcursions} className="text-foreground-600 hover:text-accent-700 cursor-pointer">{t("ex_excursions")}</Link>
          <i className="ri-arrow-right-s-line text-foreground-500"></i>
          <span className="text-foreground-500">{t(excursion.categoryKey)}</span>
          <i className="ri-arrow-right-s-line text-foreground-500"></i>
          <span className="text-foreground-950 font-medium">{t(`ex_title_${excursion.slug}`).split("—")[0].trim()}</span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left main content */}
          <div className="lg:col-span-8">
            <div className="mb-5">
              <span className="inline-block bg-accent-100 text-accent-900 text-[11px] font-medium px-2.5 py-1 rounded-full mb-3">
                {t(excursion.categoryKey)}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight mb-2">
                {t(`ex_title_${excursion.slug}`)}
              </h1>
              <p className="text-foreground-600 text-base">{t(`ex_subtitle_${excursion.slug}`)}</p>
            </div>

            <div className="flex flex-wrap items-center gap-5 mb-6 text-sm">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((i) => (
                  <i
                    key={i}
                    className={(i <= Math.floor(excursion.rating) ? "ri-star-fill" : "ri-star-line") + " text-accent-500 text-sm"}
                  ></i>
                ))}
                <span className="ml-1 font-medium text-foreground-900">{excursion.rating}</span>
                <span className="text-foreground-500">· {excursion.reviewsCount} {t("ex_reviews_word")}</span>
              </div>
              <div className="flex items-center gap-1.5 text-foreground-700">
                <i className="ri-time-line text-accent-500"></i>
                {t(`ex_dur_${excursion.slug}`)}
              </div>
              <div className="flex items-center gap-1.5 text-foreground-700">
                <i className="ri-map-pin-2-line text-primary-500"></i>
                {excursion.location}
              </div>
            </div>

            <ProductGallery images={excursion.gallery} title={excursion.title} imageFilter={excursion.imageFilter} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center text-accent-600 mb-2">
                  <i className="ri-team-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500">{t("ex_small_group")}</div>
                <div className="text-sm font-medium text-foreground-950">{t("ex_no")}</div>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center text-accent-600 mb-2">
                  <i className="ri-bus-2-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500">{t("ex_pickup")}</div>
                <div className="text-sm font-medium text-foreground-950">{excursion.pickup ? t("ex_yes") : t("ex_no")}</div>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center text-accent-600 mb-2">
                  <i className="ri-wheelchair-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500">{t("ex_accessible")}</div>
                <div className="text-sm font-medium text-foreground-950">{excursion.wheelchair ? t("ex_yes") : t("ex_no")}</div>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center text-accent-600 mb-2">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500">{t("ex_duration")}</div>
                <div className="text-sm font-medium text-foreground-950">{t(`ex_dur_${excursion.slug}`)}</div>
              </div>
            </div>

            <section className="mt-10">
              <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-list-check text-primary-500"></i>
                {t("ex_what_youll_do")}
              </h2>
              <p className="text-foreground-700 leading-relaxed">{t(`ex_tagline_${excursion.slug}`)}</p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-sparkling-line text-accent-500"></i>
                {t("ex_highlights")}
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {excursion.highlights.map((_h, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-sm text-foreground-700 leading-relaxed">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line"></i>
                    </div>
                    {t(`ex_hl_${excursion.slug}_${idx}`)}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
                <h3 className="font-heading text-lg font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                  <i className="ri-check-double-line"></i>
                  {t("ex_included")}
                </h3>
                <ul className="space-y-2">
                  {excursion.included.map((it, idx) => (
                    <li key={idx} className="text-sm text-secondary-900/90 flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                      <span>{t(`ex_inc_${excursion.slug}_${idx}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
                <h3 className="font-heading text-lg font-semibold text-accent-900 mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line"></i>
                  {t("ex_recommendations")}
                </h3>
                <ul className="space-y-2">
                  {excursion.recommendations.map((it, idx) => (
                    <li key={idx} className="text-sm text-accent-900/90 flex items-start gap-2">
                      <i className="ri-information-line text-accent-600 mt-0.5"></i>
                      <span>{t(`ex_rec_${excursion.slug}_${idx}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {excursion.requirements.length > 0 && (
              <section className="mt-8">
                <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-accent-500"></i>
                  {t("ex_requirements")}
                </h2>
                <ul className="space-y-2">
                  {excursion.requirements.map((r, idx) => (
                    <li key={idx} className="text-sm text-foreground-700 flex items-start gap-2">
                      <i className="ri-arrow-right-line text-accent-500 mt-1"></i>
                      <span>{t(`ex_req_${excursion.slug}_${idx}`)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {excursion.schedule && (
              <section className="mt-8">
                <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-calendar-schedule-line text-accent-500"></i>
                  {t("ex_schedule")}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {excursion.schedule.map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <div className="text-sm font-medium text-foreground-900">{t(`ex_sch_${excursion.slug}_${idx}`)}</div>
                      <div className="text-sm text-accent-700 font-medium">{s.time}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {excursion.hours && (
              <section className="mt-8">
                <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-time-line text-accent-500"></i>
                  {t("ex_hours")}
                </h2>
                <div className="grid grid-cols-1 gap-2">
                  {excursion.hours.map((_h, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <div className="text-sm font-medium text-foreground-900">{t(`ex_hrs_${excursion.slug}_${idx}_name`)}</div>
                      <div className="text-sm text-foreground-700">{t(`ex_hrs_${excursion.slug}_${idx}_info`)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-8">
              <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-global-line text-primary-500"></i>
                {t("ex_languages")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {excursion.languages.map((l) => (
                  <span
                    key={l}
                    className="bg-background-100 border border-background-200/70 text-foreground-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5"
                  >
                    <i className="ri-translate-2 text-primary-600"></i>
                    {l}
                  </span>
                ))}
              </div>
            </section>

            {productReviews.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-chat-quote-line text-accent-500"></i>
                  {t("ex_reviews")}
                </h2>
                <div className="space-y-4">
                  {productReviews.map((r) => (
                    <div key={r.id} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                      <div className="flex items-center gap-1 mb-2">
                        {[1,2,3,4,5].map((i) => (
                          <i
                            key={i}
                            className={(i <= r.rating ? "ri-star-fill" : "ri-star-line") + " text-accent-500 text-sm"}
                          ></i>
                        ))}
                      </div>
                      <h4 className="font-heading font-semibold text-foreground-950 mb-1.5">{t(`rev_${r.id}_title`)}</h4>
                      <p className="text-sm text-foreground-700 mb-2 leading-relaxed">{t(`rev_${r.id}_body`)}</p>
                      <div className="text-xs text-foreground-500">
                        {r.author} · {r.date}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right sticky booking */}
          <div className="lg:col-span-4">
            <BookingWidget
              variants={excursionWithPrices?.variants || []}
              slug={excursion.slug}
              title={t(`ex_title_${excursion.slug}`)}
            />
          </div>
        </div>

        <section className="mt-16 md:mt-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="inline-block text-xs uppercase tracking-[0.2em] text-accent-600 font-medium mb-2">{t("ex_related")}</span>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">{t("ex_more")}</h2>
            </div>
            <Link to="/" onClick={goToHomeExcursions} className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-accent-700 hover:text-accent-800 cursor-pointer">
              {t("ex_see_all")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {related.map((e) => (
              <ExcursionCard key={e.slug} excursion={e} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}